import { Base, send } from '@speckle/objectsender';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as THREE from 'three';
import {
  capturePhotorealisticLocal,
  geometryToSpeckleArrays,
  threeVerticesToSpeckleZUp,
} from './capturePhotorealisticLocal';
import { polygonCentroid } from './osmBuildings';
import { catalogGlbUrl, DEFAULT_SITE_PLANNER_MODEL } from '../data/sitePlannerCatalog';
import {
  getCachedOriginFloorY,
  LOT_SURFACE_BIAS,
} from './photorealisticFloor';

/** Docs: docs/SPECKLE_LIVE_SYNC.md */

const INCH_TO_M = 1 / 39.37;

/** Cached ADU parts in Three.js Y-up local space (not yet Speckle Z-up). */
const definitionCache = new Map();
let cachedContextMeshes = null;

// Bust any HMR cache that stored old BlockInstance / Y-up Speckle meshes.
definitionCache.clear();
cachedContextMeshes = null;

export const LIVE_MODEL_STORAGE_KEY = 'construx_speckle_live_model_url';
export const DEFAULT_LIVE_MODEL_NAME = 'Site planner live';

/** Photorealistic tile → Speckle capture (no remesh). Default on; set REACT_APP_SPECKLE_CAPTURE_TILES=false to disable. Internal / ToS risk. */
export function isCaptureTilesEnabled() {
  const v = process.env.REACT_APP_SPECKLE_CAPTURE_TILES;
  if (v == null || v === '') return true;
  return !['0', 'false', 'no', 'off'].includes(String(v).toLowerCase());
}

export function parseLiveModelUrl(url) {
  const raw = url || '';
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const parts = u.pathname.split('/').filter(Boolean);
    const pi = parts.indexOf('projects');
    const mi = parts.indexOf('models');
    if (pi < 0 || mi < 0) return null;
    return {
      serverUrl: `${u.protocol}//${u.host}`,
      projectId: parts[pi + 1],
      modelId: parts[mi + 1],
      url: `${u.protocol}//${u.host}/projects/${parts[pi + 1]}/models/${parts[mi + 1]}`,
    };
  } catch {
    return null;
  }
}

function storedLiveModelUrl() {
  try {
    return typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(LIVE_MODEL_STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
}

export function persistLiveModelUrl(url) {
  try {
    if (url) sessionStorage.setItem(LIVE_MODEL_STORAGE_KEY, url);
  } catch {
    /* ignore */
  }
}

/** Resolve project/model from env → session → R1AS project id (model optional). */
export function getLiveSyncConfig() {
  const token = process.env.REACT_APP_SPECKLE_TOKEN || '';
  const parsed =
    parseLiveModelUrl(process.env.REACT_APP_SPECKLE_LIVE_MODEL_URL) ||
    parseLiveModelUrl(storedLiveModelUrl()) ||
    parseLiveModelUrl(process.env.REACT_APP_SPECKLE_R1AS_URL);
  const r1 = parseLiveModelUrl(process.env.REACT_APP_SPECKLE_R1AS_URL);
  const serverUrl = parsed?.serverUrl || r1?.serverUrl || 'https://app.speckle.systems';
  const projectId = parsed?.projectId || r1?.projectId || null;
  // Only treat as selected model if LIVE url or session (not the R1AS catalog model)
  const fromEnvLive = parseLiveModelUrl(process.env.REACT_APP_SPECKLE_LIVE_MODEL_URL);
  const fromSession = parseLiveModelUrl(storedLiveModelUrl());
  const modelId = fromEnvLive?.modelId || fromSession?.modelId || null;
  return {
    serverUrl,
    projectId,
    modelId,
    token,
    captureTiles: isCaptureTilesEnabled(),
    projectUrl: projectId ? `${serverUrl}/projects/${projectId}` : null,
    modelUrl: modelId
      ? `${serverUrl}/projects/${projectId}/models/${modelId}`
      : null,
    configured: Boolean(projectId && token),
  };
}

async function gql(serverUrl, token, query, variables) {
  const response = await fetch(`${serverUrl}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join('; '));
  }
  return json.data;
}

/** List models in a Speckle project (for picker UI). */
export async function listProjectModels({
  serverUrl = 'https://app.speckle.systems',
  projectId,
  token,
  limit = 50,
} = {}) {
  if (!projectId || !token) return [];
  const data = await gql(
    serverUrl,
    token,
    `
      query ProjectModels($projectId: String!, $limit: Int!) {
        project(id: $projectId) {
          id
          name
          models(limit: $limit) {
            items { id name displayName updatedAt }
          }
        }
      }
    `,
    { projectId, limit }
  );
  const items = data?.project?.models?.items || [];
  return items.map((m) => ({
    id: m.id,
    name: m.displayName || m.name,
    updatedAt: m.updatedAt,
    url: `${serverUrl}/projects/${projectId}/models/${m.id}`,
  }));
}

function lotToPolyline(vertices, worldY = 0) {
  const yup = [];
  (vertices || []).forEach(([x, z]) => {
    yup.push(x, worldY, z);
  });
  if (vertices?.length) {
    const [x0, z0] = vertices[0];
    yup.push(x0, worldY, z0);
  }
  return new Base({
    speckle_type: 'Objects.Geometry.Polyline',
    name: 'Lot boundary',
    units: 'm',
    value: threeVerticesToSpeckleZUp(yup),
    closed: true,
  });
}

function meshBase(name, verticesZUp, faces) {
  return new Base({
    speckle_type: 'Objects.Geometry.Mesh',
    name,
    units: 'm',
    vertices: verticesZUp,
    faces,
  });
}

/** Load catalog GLB parts as Three Y-up flat vertex arrays (local space). */
async function loadDefinitionPartsThree(slug) {
  const key = slug || DEFAULT_SITE_PLANNER_MODEL;
  if (definitionCache.has(key)) return definitionCache.get(key);
  const promise = (async () => {
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync(catalogGlbUrl(key));
    const root = gltf.scene;
    root.updateWorldMatrix(true, true);
    root.scale.setScalar(INCH_TO_M);
    root.updateWorldMatrix(true, true);

    const parts = [];
    root.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry) return;
      const geom = obj.geometry.clone();
      geom.applyMatrix4(obj.matrixWorld);
      const pos = geom.getAttribute('position');
      if (!pos) {
        geom.dispose();
        return;
      }
      const vertices = [];
      for (let i = 0; i < pos.count; i += 1) {
        vertices.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      }
      const faces = [];
      if (geom.index) {
        const idx = geom.index;
        for (let i = 0; i < idx.count; i += 3) {
          faces.push(3, idx.getX(i), idx.getX(i + 1), idx.getX(i + 2));
        }
      } else {
        for (let i = 0; i < pos.count; i += 3) {
          faces.push(3, i, i + 1, i + 2);
        }
      }
      geom.dispose();
      if (vertices.length && faces.length) {
        parts.push({
          name: obj.name || `${key}_part`,
          vertices,
          faces,
        });
      }
    });
    return parts;
  })();
  definitionCache.set(key, promise);
  return promise;
}

function instanceMatrixThree(position, rotationY, worldYBase = 0) {
  const m = new THREE.Matrix4();
  const t = new THREE.Vector3(
    position[0],
    worldYBase + (position[1] || 0),
    position[2]
  );
  const q = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 1, 0),
    rotationY || 0
  );
  m.compose(t, q, new THREE.Vector3(1, 1, 1));
  return m;
}

function transformThreeVertices(vertices, matrix) {
  const v = new THREE.Vector3();
  const out = new Array(vertices.length);
  for (let i = 0; i < vertices.length; i += 3) {
    v.set(vertices[i], vertices[i + 1], vertices[i + 2]).applyMatrix4(matrix);
    out[i] = v.x;
    out[i + 1] = v.y;
    out[i + 2] = v.z;
  }
  return out;
}

/** Bake one ADU into Speckle Z-up meshes (no BlockInstance — avoids split islands). */
async function bakeStructureMeshes(structure, worldYBase) {
  const parts = await loadDefinitionPartsThree(structure.type);
  const matrix = instanceMatrixThree(
    structure.position,
    structure.rotationY,
    worldYBase
  );
  const label = String(structure.type || 'model').toUpperCase();
  return parts.map((part, i) => {
    const worldYup = transformThreeVertices(part.vertices, matrix);
    return meshBase(
      `${label} ${structure.id}${parts.length > 1 ? ` · ${part.name || i}` : ''}`,
      threeVerticesToSpeckleZUp(worldYup),
      part.faces
    );
  });
}

export function invalidateContextCache() {
  cachedContextMeshes = null;
}

export async function captureContextForSync({
  site,
  heightOffset = 0,
  surfaceY = 0,
} = {}) {
  if (!isCaptureTilesEnabled()) return null;
  const [cx, , cz] = [0, 0, 0];
  const groundMeters = site?.viewMeters || site?.groundMeters || 80;
  const result = capturePhotorealisticLocal({
    center: [cx, surfaceY + (heightOffset || 0), cz],
    groundMeters,
    surfaceY: surfaceY + (heightOffset || 0),
  });
  const { vertices, faces } = geometryToSpeckleArrays(result.geometry);
  result.geometry.dispose();
  const mesh = meshBase(
    `Photorealistic context (${Math.round(result.triangleCount)} tris)`,
    vertices,
    faces
  );
  cachedContextMeshes = {
    mesh,
    triangleCount: result.triangleCount,
    radiusM: result.radiusM,
    capturedAt: new Date().toISOString(),
  };
  return cachedContextMeshes;
}

export async function buildSceneGraph(state, { includeContext = true } = {}) {
  const {
    site,
    lot,
    structures = [],
    heightOffset = 0,
    googleAttribution = '',
  } = state;

  // Match site-planner seating: ADUs/lot sit on Photorealistic floor (+ lot.y nudge).
  const floorY = getCachedOriginFloorY() ?? 0;
  const lotWorldY = floorY + LOT_SURFACE_BIAS + (Number(lot?.y) || 0);
  // Structure position[1] is relative to SiteContentLift (= floorY).
  const aduWorldYBase = floorY;

  const elements = [];

  let contextWarning = null;
  if (includeContext && isCaptureTilesEnabled()) {
    if (!cachedContextMeshes) {
      try {
        await captureContextForSync({
          site,
          heightOffset,
          surfaceY: floorY,
        });
      } catch (err) {
        contextWarning = err?.message || String(err);
        console.warn('Photorealistic context capture skipped:', contextWarning);
      }
    }
    if (cachedContextMeshes?.mesh) {
      elements.push(cachedContextMeshes.mesh);
    }
  }

  if (lot?.vertices?.length) {
    elements.push(lotToPolyline(lot.vertices, lotWorldY));
  }

  for (const s of structures) {
    const baked = await bakeStructureMeshes(s, aduWorldYBase);
    elements.push(...baked);
  }

  const [cx, cz] = lot?.vertices?.length
    ? polygonCentroid(lot.vertices)
    : [0, 0];
  const [sx, sy, sz] = threeVerticesToSpeckleZUp([cx, lotWorldY, cz]);

  return {
    root: new Base({
      speckle_type: 'Base',
      name: `Construx site plan — ${site?.formattedAddress || 'untitled'}`,
      units: 'm',
      applicationId: 'construx:site-planner-live',
      // Speckle Z-up metadata for receivers
      coordinateSystem: 'z-up',
      sourceCoordinateSystem: 'three-y-up',
      site: {
        lat: site?.lat ?? null,
        lng: site?.lng ?? null,
        formattedAddress: site?.formattedAddress || '',
        heightOffset,
        floorY,
        lotCentroid: [sx, sy, sz],
      },
      googleAttribution: googleAttribution || '',
      captureMeta: cachedContextMeshes
        ? {
            tileCapture: true,
            radiusM: cachedContextMeshes.radiusM,
            triangleCount: cachedContextMeshes.triangleCount,
            capturedAt: cachedContextMeshes.capturedAt,
            remesh: 'none',
            internalOnly: true,
            coordinateSystem: 'z-up',
          }
        : { tileCapture: false, warning: contextWarning || undefined },
      // Single list — baked meshes + polyline (no detached BlockInstance definitions)
      elements,
    }),
    contextWarning,
  };
}

async function createVersion({
  serverUrl,
  projectId,
  modelId,
  token,
  objectId,
  message,
}) {
  const response = await fetch(`${serverUrl}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      query: `
        mutation CreateVersion($input: CreateVersionInput!) {
          versionMutations {
            create(input: $input) {
              id
              referencedObject
            }
          }
        }
      `,
      variables: {
        input: {
          projectId,
          modelId,
          objectId,
          message: message || 'Construx site planner sync',
          sourceApplication: 'Construx',
        },
      },
    }),
  });
  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join('; '));
  }
  return json.data?.versionMutations?.create;
}

/**
 * Find model by name, or create it. Never fails solely because the name exists.
 */
export async function ensureLiveModel({
  serverUrl = 'https://app.speckle.systems',
  projectId,
  token,
  name = DEFAULT_LIVE_MODEL_NAME,
  createIfMissing = true,
} = {}) {
  if (!projectId || !token) {
    throw new Error('projectId and token required to resolve a live model');
  }

  const models = await listProjectModels({ serverUrl, projectId, token });
  const existing = models.find(
    (m) => m.name === name || m.name?.toLowerCase() === name.toLowerCase()
  );
  if (existing) {
    persistLiveModelUrl(existing.url);
    return {
      serverUrl,
      projectId,
      modelId: existing.id,
      name: existing.name,
      created: false,
      url: existing.url,
    };
  }

  if (!createIfMissing) {
    throw new Error(`No Speckle model named "${name}" in this project.`);
  }

  try {
    const data = await gql(
      serverUrl,
      token,
      `
        mutation CreateModel($input: CreateModelInput!) {
          modelMutations {
            create(input: $input) { id name displayName }
          }
        }
      `,
      {
        input: {
          projectId,
          name,
          description: 'Construx internal live sync',
        },
      }
    );
    const model = data?.modelMutations?.create;
    const url = `${serverUrl}/projects/${projectId}/models/${model.id}`;
    persistLiveModelUrl(url);
    return {
      serverUrl,
      projectId,
      modelId: model.id,
      name: model.displayName || model.name,
      created: true,
      url,
    };
  } catch (err) {
    const msg = err?.message || String(err);
    // Race / legacy branch naming: re-list and adopt existing
    if (/already exists/i.test(msg)) {
      const again = await listProjectModels({ serverUrl, projectId, token });
      const hit = again.find(
        (m) => m.name === name || m.name?.toLowerCase() === name.toLowerCase()
      );
      if (hit) {
        persistLiveModelUrl(hit.url);
        return {
          serverUrl,
          projectId,
          modelId: hit.id,
          name: hit.name,
          created: false,
          url: hit.url,
        };
      }
    }
    throw err;
  }
}

/** Create a distinctly named live model (for "New stream" in the UI). */
export async function createNamedLiveModel({
  serverUrl,
  projectId,
  token,
  name,
} = {}) {
  const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const unique = name || `${DEFAULT_LIVE_MODEL_NAME} / ${stamp}`;
  const data = await gql(
    serverUrl,
    token,
    `
      mutation CreateModel($input: CreateModelInput!) {
        modelMutations {
          create(input: $input) { id name displayName }
        }
      }
    `,
    {
      input: {
        projectId,
        name: unique,
        description: 'Construx internal live sync',
      },
    }
  );
  const model = data?.modelMutations?.create;
  const url = `${serverUrl}/projects/${projectId}/models/${model.id}`;
  persistLiveModelUrl(url);
  return {
    serverUrl,
    projectId,
    modelId: model.id,
    name: model.displayName || model.name,
    created: true,
    url,
  };
}

export async function resolveLiveTarget({ modelId: preferredModelId } = {}) {
  const cfg = getLiveSyncConfig();
  if (!cfg.token) {
    throw new Error('REACT_APP_SPECKLE_TOKEN is missing (needs write access).');
  }
  if (!cfg.projectId) {
    throw new Error(
      'Set REACT_APP_SPECKLE_LIVE_MODEL_URL or REACT_APP_SPECKLE_R1AS_URL (project).'
    );
  }

  let modelId = preferredModelId || cfg.modelId;
  let serverUrl = cfg.serverUrl;
  let projectId = cfg.projectId;

  if (!modelId) {
    const ensured = await ensureLiveModel({
      serverUrl,
      projectId,
      token: cfg.token,
      name: DEFAULT_LIVE_MODEL_NAME,
    });
    modelId = ensured.modelId;
    serverUrl = ensured.serverUrl;
    projectId = ensured.projectId;
  } else {
    persistLiveModelUrl(`${serverUrl}/projects/${projectId}/models/${modelId}`);
  }

  return {
    serverUrl,
    projectId,
    modelId,
    token: cfg.token,
    url: `${serverUrl}/projects/${projectId}/models/${modelId}`,
  };
}

export async function pushLiveVersion(state, { message, forceContext, modelId: targetModelId } = {}) {
  const { serverUrl, projectId, modelId, token } = await resolveLiveTarget({
    modelId: targetModelId,
  });
  const cfg = getLiveSyncConfig();

  if (forceContext) invalidateContextCache();

  const { root, contextWarning } = await buildSceneGraph(state, {
    includeContext: cfg.captureTiles,
  });

  const result = await send(root, {
    serverUrl: serverUrl || 'https://app.speckle.systems',
    projectId,
    token,
  });

  const version = await createVersion({
    serverUrl: serverUrl || 'https://app.speckle.systems',
    projectId,
    modelId,
    token,
    objectId: result.hash,
    message:
      message ||
      `Site planner · ${state.structures?.length || 0} ADU · ${
        state.site?.formattedAddress || 'site'
      }`,
  });

  const modelUrl = `${serverUrl || 'https://app.speckle.systems'}/projects/${projectId}/models/${modelId}`;
  return {
    objectId: result.hash,
    versionId: version?.id,
    modelUrl,
    versionUrl: version?.id
      ? `${modelUrl}@${version.id}`
      : modelUrl,
    contextWarning,
    context: cachedContextMeshes
      ? {
          triangles: cachedContextMeshes.triangleCount,
          radiusM: cachedContextMeshes.radiusM,
        }
      : null,
  };
}

export function createLiveSyncScheduler(pushFn, debounceMs = 450) {
  let timer = null;
  let inflight = false;
  let pending = null;

  const run = async (payload) => {
    if (inflight) {
      pending = payload;
      return null;
    }
    inflight = true;
    try {
      return await pushFn(payload);
    } finally {
      inflight = false;
      if (pending) {
        const next = pending;
        pending = null;
        return run(next);
      }
    }
  };

  return {
    schedule(payload) {
      clearTimeout(timer);
      return new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          run(payload).then(resolve).catch(reject);
        }, debounceMs);
      });
    },
    flush(payload) {
      clearTimeout(timer);
      return run(payload);
    },
  };
}
